import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  GetEventDetailedDocument,
  SearchMyFriendsQuery,
  GetEventDetailedQuery,
  useSearchMyFriendsLazyQuery,
  useUpdateEventParticipantsMutation,
} from '@graphql';
import { truncateText } from '@utils';
import { useToastError } from '@hooks';
import { WrapperContext } from './layout';

interface ParticipantsManageProps {
  eventId: string;
  creatorId: string;
  participants: GetEventDetailedQuery['event']['participants']['list'];
}

export function ParticipantsManage(props: Readonly<ParticipantsManageProps>) {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('ParticipantsManage must be used inside Wrapper');
  }

  const { isManaging, setIsManaging, isSaving, setIsSaving, isSaveDisabled, setIsSaveDisabled } = context;
  const { eventId, creatorId, participants } = props;
  const theme = useTheme();
  const { t } = useTranslation('field');
  const toastError = useToastError();

  const initialSelected = React.useMemo(
    () =>
      participants
        .filter((participant) => participant.account?.id !== creatorId)
        .map((p) => {
          return {
            id: p.account?.id ?? '',
            name: p.account?.name ?? '',
            slug: p.account?.slug ?? '',
            avatar: p.account?.avatar ?? null,
          };
        }),
    [participants, creatorId]
  );
  const [selected, setSelected] = React.useState<SearchMyFriendsQuery['searchMyFriends']>(initialSelected);

  React.useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  const [updateEventParticipants] = useUpdateEventParticipantsMutation();

  const formik = useFormik({
    initialValues: {
      participantIds: selected.map((participant) => participant.id),
    },
    validationSchema: Yup.object({
      participantIds: Yup.array().of(Yup.string()).nullable(),
    }),
    onSubmit: async (values) => {
      try {
        await updateEventParticipants({
          variables: {
            eventId,
            participantIds: values.participantIds,
          },
          refetchQueries: [{ query: GetEventDetailedDocument, variables: { id: eventId } }],
          awaitRefetchQueries: true,
        });
        setIsSaving(false);
        setIsManaging(null);
      } catch (error) {
        setIsSaving(false);
        toastError(error, t('event.toast.update.error', { ns: 'field' }));
      }
    },
  });

  React.useEffect(() => {
    if (isSaving) {
      formik.submitForm();
    }
  }, [isSaving]);

  React.useEffect(() => {
    if (!isManaging) {
      formik.resetForm({
        values: {
          participantIds: initialSelected.map((participant) => participant.id),
        },
      });
    }
  }, [isManaging]);

  React.useEffect(() => {
    formik.setValues({
      participantIds: selected.map((participant) => participant.id),
    });
  }, [selected]);

  React.useEffect(() => {
    if (isSaveDisabled && formik.isValid && formik.dirty) {
      setIsSaveDisabled(false);
    } else if (!isSaveDisabled && (!formik.isValid || !formik.dirty)) {
      setIsSaveDisabled(true);
    }
  }, [isSaveDisabled, formik.isValid, formik.dirty]);

  return (
    <Stack spacing={3}>
      <SearchFriends creatorId={creatorId} selected={selected} setSelected={setSelected} />
      {selected.map((friend) => (
        <Stack
          key={friend.id}
          direction="row"
          spacing={1}
          alignItems="center"
          width="100%"
          divider={<Divider />}
        >
          <AvatarWithSkeleton
            src={friend.avatar?.url}
            alt={friend.name}
            sx={{ width: { xs: 30, sm: 40 }, height: { xs: 30, sm: 40 } }}
          />
          <Stack width="100%">
            <Typography fontWeight="bold" fontSize={{ xs: '.8rem', sm: '.9rem' }}>
              {truncateText(friend.name, 25)}
            </Typography>
            <Typography color="textSecondary" fontSize={{ xs: '.7rem', sm: '.8rem' }}>
              @{truncateText(friend.slug, 25)}
            </Typography>
          </Stack>
          <IconButton
            data-friend-id={friend.id}
            onClick={() => setSelected((prev) => prev.filter(({ id }) => id !== friend.id))}
            sx={{
              width: { xs: 30, sm: 40 },
              height: { xs: 30, sm: 40 },
              backgroundColor: '#424242',
              '&:hover': { backgroundColor: 'darkred' },
            }}
          >
            <theme.icons.close
              sx={{
                color: 'lightgrey',
                '&:hover': { color: 'white' },
                width: { xs: 20, sm: 30 },
                height: { xs: 20, sm: 30 },
              }}
            />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );
}

interface SearchFriendsProps {
  creatorId: string;
  selected: SearchMyFriendsQuery['searchMyFriends'];
  setSelected: React.Dispatch<React.SetStateAction<SearchMyFriendsQuery['searchMyFriends']>>;
}

function SearchFriends(props: Readonly<SearchFriendsProps>) {
  const { creatorId, selected, setSelected } = props;
  const [options, setOptions] = React.useState<SearchMyFriendsQuery['searchMyFriends']>([]);
  const [inputValue, setInputValue] = React.useState('');
  const { t } = useTranslation('cta');

  const [searchMyFriends, { loading }] = useSearchMyFriendsLazyQuery({
    onCompleted: ({ searchMyFriends }) => {
      if (!inputValue.trim().length) {
        // sort options with selected on top
        const sortedOptions = [...searchMyFriends]
          .filter((_f, idx) => idx < 10) // first ten
          .sort((a, b) => {
            const aSelected = selected.some((participant) => participant.id === a.id);
            const bSelected = selected.some((participant) => participant.id === b.id);

            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return 0;
          });
        setOptions(sortedOptions);
      } else {
        setOptions(searchMyFriends.filter((option) => option.id !== creatorId));
      }
    },
  });

  const handleSearch = React.useMemo(
    () =>
      _.debounce((searchValue: string) => {
        searchMyFriends({ variables: { text: searchValue.trim() } });
      }, 300),
    [searchMyFriends]
  );

  React.useEffect(() => {
    handleSearch(inputValue);

    return () => {
      handleSearch.cancel();
    };
  }, [inputValue, handleSearch]);

  return (
    <Autocomplete
      sx={{ my: 3 }}
      multiple
      fullWidth
      autoFocus
      disableClearable
      options={options}
      value={selected}
      inputValue={inputValue}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      disableCloseOnSelect
      onChange={(_, newValue) => {
        setSelected(newValue.filter((option) => option.id !== creatorId));
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'removeOption') return;
        if (reason === 'selectOption') return;

        setInputValue(newInputValue);

        if (!newInputValue.trim()) {
          setOptions([]);
        }
      }}
      renderOption={(props, option, { selected }) => {
        const { key, id, ...optionProps } = props;
        const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;

        return (
          <li key={key + id} {...optionProps}>
            <Stack direction="row" spacing={1} alignItems="center" width="100%" divider={<Divider />}>
              <AvatarWithSkeleton
                src={option.avatar?.url}
                alt={option.name}
                sx={{ width: { xs: 30, sm: 50 }, height: { xs: 30, sm: 50 } }}
              />
              <Stack width="100%">
                <Typography fontWeight="bold" fontSize={{ xs: '.8rem', sm: '.9rem' }}>
                  {truncateText(option.name, 25)}
                </Typography>
                <Typography color="textSecondary" fontSize={{ xs: '.7rem', sm: '.8rem' }}>
                  @{truncateText(option.slug, 25)}
                </Typography>
              </Stack>
            </Stack>
            <SelectionIcon
              fontSize="small"
              style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }}
            />
          </li>
        );
      }}
      renderTags={() => null}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={t('search', { ns: 'cta' })}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
    />
  );
}

interface AvatarWithSkeletonProps {
  src?: string | null;
}

function AvatarWithSkeleton(props: Readonly<AvatarWithSkeletonProps & AvatarProps>) {
  const [loaded, setLoaded] = React.useState(false);
  const { src, alt, sx } = props;

  return (
    <Box position="relative" sx={sx}>
      {!loaded && <Skeleton variant="circular" sx={sx} animation="wave" />}

      <Avatar
        src={src ?? undefined}
        alt={alt}
        imgProps={{
          onLoad: () => setLoaded(true),
          onError: () => setLoaded(true),
        }}
        sx={{
          ...sx,
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: loaded ? 1 : 0,
        }}
      />
    </Box>
  );
}
